"use client";

import { CheckCircle2, Clock, DollarSign, ShieldCheck } from "lucide-react";

const tokenPool = [
  "call",
  "terac_request_feasibility",
  '{ "role":',
  '"Senior eng" }',
  "request_id: fr_8f2",
  "cpi_usd: 28",
  "status: PRICED",
  "terac_launch_draft",
  "status: APPROVED",
  "gov_id ✓",
  "work_email ✓",
  "terac_get_submissions",
  "verified: true",
  "payout: on_complete",
  "count: 3",
];

const columns = [
  { left: "6%", duration: 26, size: 12, start: 0 },
  { left: "23%", duration: 34, size: 11, start: 5 },
  { left: "44%", duration: 30, size: 12, start: 9 },
  { left: "66%", duration: 38, size: 11, start: 2 },
  { left: "86%", duration: 28, size: 12, start: 6 },
];

const tickerTools = [
  "terac_list_opportunities",
  "terac_request_feasibility",
  "terac_launch_draft_opportunity",
  "terac_get_submissions",
  "terac_get_context",
  "terac_pause_opportunity",
];

const resultChips = [
  {
    icon: DollarSign,
    label: "quote $84 · eta 6h",
    top: "20%",
    left: "3%",
    delay: 0,
  },
  {
    icon: CheckCircle2,
    label: "status: APPROVED",
    top: "30%",
    right: "3%",
    delay: 1.4,
  },
  {
    icon: ShieldCheck,
    label: "gov_id · work_email",
    bottom: "34%",
    left: "3%",
    delay: 0.8,
  },
  {
    icon: Clock,
    label: "delivered in 5h 12m",
    bottom: "30%",
    right: "4%",
    delay: 2.1,
  },
];

function RainColumn({ left, duration, size, start }: (typeof columns)[number]) {
  const rotated = [...tokenPool.slice(start), ...tokenPool.slice(0, start)];
  // Doubled so the loop wraps seamlessly; precompute keys to avoid index keys.
  const items = [...rotated, ...rotated].map((token, idx) => ({
    token,
    key: `${token}-${idx}`,
  }));
  return (
    <div className="absolute top-0 h-full" style={{ left }}>
      <div
        className="mcpt-rain flex flex-col gap-6 font-mono whitespace-nowrap text-white/10"
        style={{ fontSize: size, animationDuration: `${duration}s` }}
      >
        {items.map((item) => (
          <span key={item.key}>{item.token}</span>
        ))}
      </div>
    </div>
  );
}

const tickerItems = [...tickerTools, ...tickerTools, ...tickerTools].map(
  (tool, idx) => ({ tool, key: `${tool}-${idx}` }),
);

/**
 * Animated hero artwork for the MCP landing page. Faint columns of tool-call
 * tokens rain downward, a slow ticker of tool names scrolls across the top, and
 * verified result chips drift upward and fade. Reads as the live stream of an
 * agent reaching for humans and getting structured results back. All motion is
 * disabled under prefers-reduced-motion.
 */
export function MCPHeroArtwork() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <style>{`
        @keyframes mcpt-rain { from { transform: translateY(0); } to { transform: translateY(-50%); } }
        @keyframes mcpt-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes mcpt-rise { 0% { transform: translateY(10px); opacity: 0; } 18%,72% { opacity: 1; } 100% { transform: translateY(-22px); opacity: 0; } }
        .mcpt-rain { animation-name: mcpt-rain; animation-timing-function: linear; animation-iteration-count: infinite; }
        .mcpt-marquee { animation: mcpt-marquee 38s linear infinite; }
        .mcpt-rise { animation: mcpt-rise 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .mcpt-rain,.mcpt-marquee,.mcpt-rise { animation: none !important; }
        }
      `}</style>

      {/* Token rain */}
      {columns.map((col) => (
        <RainColumn key={col.left} {...col} />
      ))}

      {/* Soft glow behind the headline to keep copy legible */}
      <div className="bg-marketing-blue-dark/40 absolute top-1/2 left-1/2 h-[420px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />

      {/* Tool-name ticker near the top */}
      <div className="absolute top-[3%] left-0 w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
        <div className="mcpt-marquee flex w-max items-center gap-3">
          {tickerItems.map((item) => (
            <span
              key={item.key}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-xs text-white/55"
            >
              <span className="bg-marketing-blue-light h-1.5 w-1.5 rounded-full" />
              {item.tool}
            </span>
          ))}
        </div>
      </div>

      {/* Drifting verified-result chips */}
      {resultChips.map((chip) => (
        <div
          key={chip.label}
          className="mcpt-rise absolute hidden md:block"
          style={{
            top: chip.top,
            bottom: chip.bottom,
            left: chip.left,
            right: chip.right,
            animationDelay: `${chip.delay}s`,
          }}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 font-mono text-xs text-white/85 backdrop-blur-sm">
            <chip.icon
              className="h-3.5 w-3.5 text-emerald-400"
              strokeWidth={2.5}
            />
            {chip.label}
          </span>
        </div>
      ))}
    </div>
  );
}
